import { FC } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ToDateTime } from '@utils/DateUtils';
import { PrizeDrawingWeek, useAdminPrizeDrawingsViewCreator } from '@app/view-creator/AdminPrizeDrawingsViewCreator';

const formatDrawnAt = (drawnAt: string | null): string => (drawnAt ? ToDateTime(drawnAt).toFormat('LLL d, yyyy') : '—');

const weekLabel = (week: PrizeDrawingWeek): string =>
  week.weekNumber === null ? 'Unknown week' : `Week ${week.weekNumber}`;

export const AdminPrizeDrawings: FC = () => {
  const navigate = useNavigate();
  const groupId = Number(useParams().groupId);
  const { data, isLoading, isError } = useAdminPrizeDrawingsViewCreator(groupId);

  if (isLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="section" sx={{ py: 2 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin')} sx={{ mb: 1 }}>
        Back to Admin
      </Button>
      <Typography variant="h4">Prize Drawings</Typography>
      {data && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {data.cohortName}
        </Typography>
      )}

      {isError || !data ? (
        <Alert severity="error">The prize drawings for this group could not be loaded.</Alert>
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Weekly Winners
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each week, one gift card is drawn from the participants who met their goal and have the fewest previous
              wins in this group. Program week 1 is the first week after onboarding.
            </Typography>
            {data.weeks.length === 0 ? (
              <Typography color="text.secondary">No drawings have run for this group yet.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Week</TableCell>
                    <TableCell>Winner</TableCell>
                    <TableCell>Drawn</TableCell>
                    <TableCell>Gift Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.weeks.map((week) => (
                    <TableRow key={week.winner?.prizeId ?? `week-${week.weekNumber}`}>
                      <TableCell>{weekLabel(week)}</TableCell>
                      <TableCell>
                        {week.winner ? (
                          week.winner.name
                        ) : (
                          <Typography variant="body2" color="text.secondary" component="span">
                            No winner — nobody met their goal or the drawing did not run
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDrawnAt(week.winner?.drawnAt ?? null)}</TableCell>
                      <TableCell>
                        {week.winner &&
                          (week.winner.hasGiftLink ? (
                            <Chip label="Created" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Failed — coach follow-up" size="small" color="warning" variant="outlined" />
                          ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>

          <Box>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Participant Wins
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Wins only count within this team. Custom gift cards are not drawing wins.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Participant</TableCell>
                  <TableCell align="right">Total Wins</TableCell>
                  <TableCell>Weeks Won</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.participants.map((participant) => (
                  <TableRow key={participant.cohortUserId} sx={{ opacity: participant.isActive ? 1 : 0.5 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {participant.name}
                        {!participant.isActive && <Chip label="Inactive" size="small" variant="outlined" />}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{participant.winCount}</TableCell>
                    <TableCell>
                      {participant.winningWeeks.length > 0 ? participant.winningWeeks.join(', ') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Container>
  );
};
